import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AnimatedIcon } from '../../components/icons';
import { useDealStore } from '../../store/dealStore';
import { SURVEY_SERVICE_TYPES } from '../../types';
import { WEB_STATUS_COLORS } from '../../utils/statusPresentation';

const CSS = `
.zs-stat-card {
  position: relative;
  overflow: hidden;
  height: 100%;
  border-radius: var(--sc-radius-lg);
  border: 1px solid var(--sc-border);
  background: var(--sc-bg-secondary);
  box-shadow: var(--sc-shadow-sm);
  padding: 10px 14px;
  text-decoration: none;
  display: block;
  transition: all var(--sc-transition-normal);
}
.zs-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--sc-shadow-md);
}
.zs-stat-card-circle {
  position: absolute;
  top: -16px;
  right: -16px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.zs-stat-card:hover .zs-stat-card-circle {
  transform: scale(1.15);
}
.zs-stat-card-iconbox {
  width: 32px;
  height: 32px;
  border-radius: var(--sc-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.zs-stat-card:hover .zs-stat-card-iconbox {
  transform: scale(1.06) rotate(-3deg);
}
`;

const STATUS_COLORS = WEB_STATUS_COLORS;

const getCompactStatusText = (status: string) => {
  switch (status) {
    case 'UNDER_REVIEW': return '🟡 Review';
    case 'ASSIGNED': return '🔵 Assigned';
    case 'NEGOTIATING': return '🟡 Negotiating';
    case 'CONFIRMED': return '🟢 Confirmed';
    case 'CLOSED': return '🟢 Closed';
    case 'REQUEST_SUBMITTED': return '🔵 Submitted';
    case 'APPROVED': return '🟢 Approved';
    case 'DISPUTED': return '🟠 Disputed';
    case 'REJECTED': return '🔴 Rejected';
    default: return status.replace(/_/g, ' ');
  }
};

export const SurveyorSurveyHistory: React.FC = () => {
  const { deals, fetchDeals } = useDealStore();
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchDeals().finally(() => setIsLoading(false));
  }, []);

  const historyItems = useMemo(() => {
    let items = [...deals];

    if (statusFilter !== 'ALL') {
      items = items.filter((d) => d.status === statusFilter);
    }
    if (serviceTypeFilter) {
      items = items.filter((d) => d.request?.service_type === serviceTypeFilter);
    }
    if (dateFrom) {
      items = items.filter((d) => d.created_at >= dateFrom);
    }
    if (dateTo) {
      const end = dateTo + 'T23:59:59';
      items = items.filter((d) => d.created_at <= end);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter((d) =>
        (d.request?.address || '').toLowerCase().includes(q) ||
        (d.request?.service_type || '').toLowerCase().includes(q) ||
        (d.client?.first_name || '').toLowerCase().includes(q) ||
        (d.client?.last_name || '').toLowerCase().includes(q) ||
        d.id.toLowerCase().includes(q)
      );
    }

    items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return items;
  }, [deals, statusFilter, serviceTypeFilter, dateFrom, dateTo, search]);

  const clearFilters = () => {
    setStatusFilter('ALL');
    setServiceTypeFilter('');
    setDateFrom('');
    setDateTo('');
    setSearch('');
  };

  // Stats
  const totalJobs = deals.length;
  const activeJobs = deals.filter((d) => ['ASSIGNED', 'NEGOTIATING'].includes(d.status)).length;
  const confirmedJobs = deals.filter((d) => d.status === 'CONFIRMED').length;
  const totalEarnings = deals
    .filter((d) => d.status === 'CONFIRMED' || d.status === 'CLOSED')
    .reduce((sum, d) => sum + (d.deal_value || 0) - (d.expected_commission || 0), 0);

  if (isLoading) {
    return <div style={s.center}><div style={s.spinner} /></div>;
  }

  return (
    <div>
      <style>{CSS}</style>
      <h1 style={s.title}>Job History</h1>
      <p style={s.subtitle}>Overview of all survey assignments, outcomes, and earnings.</p>

      {/* KPIs */}
      <div style={s.kpiRow}>
        {[
          { label: 'Total Jobs', val: totalJobs.toString(), icon: 'clipboard', color: '#0C5ADB' },
          { label: 'Active', val: activeJobs.toString(), icon: 'tools', color: '#FDCB6E' },
          { label: 'Confirmed', val: confirmedJobs.toString(), icon: 'success', color: '#10B981' },
          { label: 'Net Earn', val: `$${totalEarnings.toFixed(2)}`, icon: 'money', color: '#6C5CE7' },
        ].map((k) => (
          <div key={k.label} className="zs-stat-card">
            {/* Top color bar */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 3,
              background: `linear-gradient(90deg, ${k.color}, ${k.color}88)`
            }} />
            {/* Decorative circle */}
            <div className="zs-stat-card-circle" style={{ backgroundColor: `${k.color}0A` }} />
            
            {/* Content inner */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1, gap: 4, textAlign: 'center' }}>
              <div className="zs-stat-card-iconbox" style={{ background: `linear-gradient(135deg, ${k.color}15 0%, ${k.color}08 100%)`, color: k.color, border: `1px solid ${k.color}12`, margin: '0 auto' }}>
                <AnimatedIcon name={k.icon as any} size={16} color={k.color} />
              </div>
              <div style={{ fontSize: 9, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 1 }}>
                {k.label}
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em', lineHeight: 1 }}>
                {k.val}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={s.filterRow}>
        <input
          placeholder="Search client, location, ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={s.searchInput}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={s.select}>
          <option value="ALL">All Statuses</option>
          {['ASSIGNED', 'NEGOTIATING', 'CONFIRMED', 'CLOSED', 'DISPUTED'].map((st) => (
            <option key={st} value={st}>{st.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <select value={serviceTypeFilter} onChange={(e) => setServiceTypeFilter(e.target.value)} style={s.select}>
          <option value="">All Service Types</option>
          {SURVEY_SERVICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={s.dateInput} title="From date" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={s.dateInput} title="To date" />
        <button onClick={clearFilters} style={s.clearBtn}>Clear</button>
      </div>

      <div style={s.resultCount}>Showing {historyItems.length} of {totalJobs} jobs</div>

      {/* Card List */}
      {historyItems.length === 0 ? (
        <div style={s.empty}>No job records match your filters.</div>
      ) : (
        <div style={s.cardList}>
          {historyItems.map((deal) => {
            const netPay = (deal.deal_value || 0) - (deal.expected_commission || 0);
            const statusColor = STATUS_COLORS[deal.status] || STATUS_COLORS.CLOSED;
            return (
              <Link
                to={`/surveyor/deal/${deal.id}`}
                key={deal.id}
                style={{
                  ...s.card,
                  borderLeft: `4px solid ${statusColor.border || 'var(--sc-border)'}`
                }}
                className="sc-hover-scale"
              >
                <div style={s.cardHeader}>
                  <div>
                    <div style={s.serviceType}>{deal.request?.service_type || 'Job Request'}</div>
                    <div style={s.address}>📍 {deal.request?.address || 'Location unspecified'}</div>
                  </div>
                  <span style={{
                    ...s.statusBadge,
                    background: statusColor.bg,
                    color: statusColor.color,
                    borderColor: statusColor.border,
                  }}>
                    {getCompactStatusText(deal.status)}
                  </span>
                </div>

                <div style={s.cardBody}>
                  <div style={s.infoRow}>
                    <span style={s.infoLabel}>Client</span>
                    <span style={s.infoVal}>{deal.client?.first_name} {deal.client?.last_name}</span>
                  </div>
                  <div style={s.infoRow}>
                    <span style={s.infoLabel}>Value</span>
                    <span style={{ ...s.infoVal, fontWeight: 700 }}>
                      {deal.deal_value != null ? `$${deal.deal_value.toFixed(2)}` : '—'}
                    </span>
                  </div>
                  <div style={s.infoRow}>
                    <span style={s.infoLabel}>Commission</span>
                    <span style={{ ...s.infoVal, color: 'var(--sc-error)' }}>
                      {deal.expected_commission != null ? `-$${deal.expected_commission.toFixed(2)}` : '—'}
                    </span>
                  </div>
                  <div style={s.infoRow}>
                    <span style={s.infoLabel}>Net Rev</span>
                    <span style={{ ...s.infoVal, fontWeight: 700, color: 'var(--sc-success)' }}>
                      {deal.deal_value != null ? `$${netPay.toFixed(2)}` : '—'}
                    </span>
                  </div>
                  <div style={s.infoRow}>
                    <span style={s.infoLabel}>Date</span>
                    <span style={s.infoVal}>
                      {new Date(deal.created_at).toLocaleDateString('en-ZW', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  <div style={s.infoRow}>
                    <span style={s.infoLabel}>ID</span>
                    <span style={{ ...s.infoVal, fontFamily: 'monospace', fontSize: 11 }}>
                      #{deal.id.slice(0, 8).toUpperCase()}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  center: { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'sc-spin 0.8s linear infinite' },
  title: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.5px' },
  subtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: '0 0 20px', fontFamily: "'Inter', sans-serif" },
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 20 },
  kpiCard: { display: 'block', background: 'var(--sc-bg-secondary)', borderRadius: 16, padding: '20px 22px', border: '1px solid var(--sc-border)', textAlign: 'center' as const, boxShadow: 'var(--sc-shadow-sm)' },
  kpiVal: { fontSize: 24, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '6px 0 2px' },
  kpiLabel: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 600, textTransform: 'uppercase' as const },
  filterRow: { display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' as const, alignItems: 'center' },
  searchInput: { flex: 2, minWidth: 180, padding: '9px 12px', border: '1px solid var(--sc-border)', borderRadius: 8, fontSize: 13, fontFamily: "'Inter', sans-serif", color: 'var(--sc-text-primary)', background: 'var(--sc-bg-secondary)' },
  select: { padding: '9px 10px', border: '1px solid var(--sc-border)', borderRadius: 8, fontSize: 13, background: 'var(--sc-bg-secondary)', color: 'var(--sc-text-primary)' },
  dateInput: { padding: '9px 10px', border: '1px solid var(--sc-border)', borderRadius: 8, fontSize: 13, color: 'var(--sc-text-primary)', background: 'var(--sc-bg-secondary)' },
  clearBtn: { padding: '9px 14px', background: 'var(--sc-bg-secondary)', border: '1px solid var(--sc-border)', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: 'var(--sc-text-secondary)', transition: 'all 0.2s' },
  resultCount: { fontSize: 12, color: 'var(--sc-text-secondary)', marginBottom: 14 },
  empty: { textAlign: 'center' as const, padding: 60, color: 'var(--sc-text-secondary)', background: 'var(--sc-bg-secondary)', borderRadius: 16, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-sm)' },
  cardList: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: { display: 'block', background: 'var(--sc-bg-secondary)', borderRadius: 16, padding: '12px 16px', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-sm)', textDecoration: 'none', color: 'inherit', transition: 'all var(--sc-transition-fast)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  serviceType: { fontSize: 16, fontWeight: 700, color: 'var(--sc-text-primary)', fontFamily: "'Outfit', sans-serif" },
  address: { fontSize: 12, color: 'var(--sc-text-secondary)', marginTop: 2 },
  statusBadge: { padding: '4px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' as const, whiteSpace: 'nowrap' as const, border: '1px solid' },
  cardBody: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '6px 12px' },
  infoRow: { display: 'flex', flexDirection: 'column', gap: 2 },
  infoLabel: { fontSize: 10, fontWeight: 700, color: 'var(--sc-text-tertiary)', textTransform: 'uppercase' as const, letterSpacing: '0.3px' },
  infoVal: { fontSize: 13, color: 'var(--sc-text-primary)', fontWeight: 500 },
};
